import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { getAuctionPackages } from "@/lib/api";
import useCountdown, { isRolling } from "@/lib/useCountdown";
import ApplicationForm from "@/components/site/ApplicationForm";

// Command buttons → package slug (or route)
const COMMANDS = [
  { id: "chicago",       label: "Chicago",              slug: "chicago-2026",         grad: "from-[#c8102e] via-[#0075c9] to-[#e8c876]" },
  { id: "summer-twenty", label: "Summer Twenty",        slug: "chicago-summer-2027",  grad: "from-[#ffd400] via-[#0075c9] to-[#e8c876]" },
  { id: "grammy",        label: "Grammy",               slug: "la-grammy-2027",       grad: "from-[#7a1fbf] via-[#ff2ea8] to-[#e8c876]" },
  { id: "super-bowl",    label: "Super Bowl",           slug: "la-superbowl-2027",    grad: "from-[#0075c9] via-[#003a70] to-[#e8c876]" },
  { id: "dubai",         label: "Dubai",                slug: "dubai-2027",           grad: "from-[#0f5d3a] via-[#1a7a4f] to-[#e8c876]" },
  { id: "nairobi",       label: "Nairobi",              slug: "nairobi-2027",         grad: "from-[#0f5d3a] via-[#c8102e] to-[#e8c876]" },
  { id: "bali",          label: "Bali",                 slug: "bali-2027",            grad: "from-[#0fa675] via-[#00b7d6] to-[#ff70b6]" },
  { id: "vegas",         label: "Vegas",                slug: "vegas-superzoo-2027",  grad: "from-[#ff2ea8] via-[#7a1fbf] to-[#e8c876]" },
  { id: "private",       label: "Private",              slug: "private-inperson",     grad: "from-[#c8102e] via-[#e8c876] to-[#ffffff]" },
  { id: "online",        label: "Online",               slug: "online-private",       grad: "from-[#3d6bff] via-[#7a3dff] to-[#ffffff]" },
  { id: "group",         label: "Group Class",          slug: "online-group",         grad: "from-[#a4d800] via-[#ff8a1e] to-[#3d6bff]" },
  { id: "bring",         label: "Bring Toriano To My City", route: "/work-with-toriano?type=event", grad: "from-[#0a0908] via-[#4b2e83] to-[#c9a24b]" },
];

const IMAGES = {
  "chicago-2026":         "https://images.unsplash.com/photo-1494522358652-f30e61a60313?w=1400&q=85&auto=format&fit=crop",
  "chicago-summer-2027":  "https://images.unsplash.com/photo-1477884213360-7e9d7dcc1e48?w=1400&q=85&auto=format&fit=crop",
  "la-grammy-2027":       "https://images.unsplash.com/photo-1519741497674-611481863552?w=1400&q=85&auto=format&fit=crop",
  "la-superbowl-2027":    "https://images.unsplash.com/photo-1508672019048-805c876b67e2?w=1400&q=85&auto=format&fit=crop",
  "dubai-2027":           "https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=1400&q=85&auto=format&fit=crop",
  "nairobi-2027":         "https://images.unsplash.com/photo-1516426122078-c23e76319801?w=1400&q=85&auto=format&fit=crop",
  "bali-2027":            "https://images.unsplash.com/photo-1537996194471-e657df975ab4?w=1400&q=85&auto=format&fit=crop",
  "vegas-superzoo-2027":  "https://images.unsplash.com/photo-1605833556294-ea5c7a74f57d?w=1400&q=85&auto=format&fit=crop",
  "austin-2027":          "https://images.unsplash.com/photo-1531058020387-3be344556be6?w=1400&q=85&auto=format&fit=crop",
  "orlando-2027":         "https://images.unsplash.com/photo-1597466599360-3b9775841aec?w=1400&q=85&auto=format&fit=crop",
  "miami-basel-2027":     "https://images.unsplash.com/photo-1535498730771-e735b998cd64?w=1400&q=85&auto=format&fit=crop",
  "online-private":       "https://images.unsplash.com/photo-1583337130417-3346a1be7dee?w=1400&q=85&auto=format&fit=crop",
  "online-group":         "https://images.unsplash.com/photo-1587300003388-59208cc962cb?w=1400&q=85&auto=format&fit=crop",
  "private-inperson":     "https://images.unsplash.com/photo-1568572933382-74d440642117?w=1400&q=85&auto=format&fit=crop",
};

function Countdown({ target }) {
  const { d, h, m, s, done } = useCountdown(target);
  if (isRolling(target)) {
    return (
      <div className="rounded-2xl bg-[#0f5d3a] text-white px-4 py-3 text-center">
        <div className="text-[10px] tracking-[0.24em] uppercase opacity-80">Availability</div>
        <div className="stat-num text-2xl">Always On</div>
      </div>
    );
  }
  if (done) return (
    <div className="rounded-2xl bg-black text-white px-4 py-3 text-center">
      <div className="text-[10px] tracking-[0.24em] uppercase opacity-80">Window</div>
      <div className="stat-num text-2xl">Closed</div>
    </div>
  );
  return (
    <div className="grid grid-cols-4 gap-2">
      {[["D", d], ["H", h], ["M", m], ["S", s]].map(([l, v]) => (
        <div key={l} className="rounded-2xl bg-black text-white px-2 py-3 text-center">
          <div className="stat-num text-xl md:text-2xl">{String(v).padStart(2, "0")}</div>
          <div className="text-[9px] tracking-[0.24em] uppercase opacity-80 mt-0.5">{l}</div>
        </div>
      ))}
    </div>
  );
}

export default function Live() {
  const [pkgs, setPkgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [params, setParams] = useSearchParams();
  const nav = useNavigate();
  const [modal, setModal] = useState(false);
  const [qr, setQr] = useState(false);
  const detailRef = useRef(null);

  const active = params.get("pkg") || "chicago-2026";

  const load = useCallback(async () => {
    try {
      const data = await getAuctionPackages();
      setPkgs(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    document.title = "LIVE · Global Wolfkeeper Auction · Toriano Sanzone";
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [load]);

  const pkg = useMemo(() => pkgs.find((p) => p.slug === active), [pkgs, active]);
  const soldOut = !!(pkg && pkg.has_capacity && pkg.remaining_slots <= 0);

  const pick = (cmd) => {
    if (cmd.route) { nav(cmd.route); return; }
    setParams({ pkg: cmd.slug });
    setTimeout(() => detailRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 120);
  };

  const openApply = () => {
    if (!pkg) return;
    if (soldOut) { toast.error("This package is sold out. Pick another below."); return; }
    setModal(true);
  };

  return (
    <div className="min-h-screen bg-[#f7f1e5] pb-32" data-testid="live-page">
      {/* Top hero */}
      <section className="relative overflow-hidden bg-[#0a0908] text-[#f7f1e5]">
        <div className="absolute inset-0 opacity-70" style={{
          background: "radial-gradient(600px 400px at 100% 0%, rgba(201,162,75,0.35), transparent 60%), radial-gradient(500px 400px at 0% 100%, rgba(255,46,168,0.25), transparent 60%), radial-gradient(500px 400px at 50% 100%, rgba(75,46,131,0.35), transparent 60%)"
        }}/>
        <div className="relative z-10 max-w-[1200px] mx-auto px-5 pt-8 pb-10 md:pt-12">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 text-[#e8c876]" data-testid="live-home">
              <span className="wolf-mark text-xl">TS</span>
              <span className="text-[10px] tracking-[0.28em] uppercase">Home</span>
            </Link>
            <span className="inline-flex items-center gap-2 rounded-full bg-[#c8102e] text-white px-3 py-1.5 text-[10px] tracking-[0.28em] uppercase font-bold">
              <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"/> Live
            </span>
          </div>

          <h1 className="mt-6 font-display text-[clamp(2rem,8vw,4.5rem)] leading-[0.95] tracking-[-0.02em]" data-testid="live-headline">
            Welcome to the <span className="italic" style={{
              background: "linear-gradient(90deg,#e8c876 0%, #ff2ea8 55%, #c9a24b 100%)",
              WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent",
            }}>Global Wolfkeeper Auction.</span>
          </h1>

          <div className="mt-5 flex items-center gap-4">
            <img src="https://customer-assets-7cd3h4nn.emergentagent.net/job_golden-wolf-premium/artifacts/0ag6fthu_WhatsApp%20Image%202026-09-01%20at%2021.43.26.jpeg" alt="Toriano Sanzone — Leader of the Pack" className="w-16 h-16 md:w-20 md:h-20 rounded-2xl object-cover border border-[#e8c876]/40 shadow-lg" data-testid="live-poster"/>
            <p className="text-lg md:text-xl text-[#f7f1e5]/85 font-editorial italic">
              Jalimo and Moraa are live. Choose your training experience below.
            </p>
          </div>

          {/* Command grid */}
          <div className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2.5" data-testid="command-grid">
            {COMMANDS.map((c) => {
              const p = pkgs.find((x) => x.slug === c.slug);
              const isOut = p && p.has_capacity && p.remaining_slots <= 0;
              const isActive = c.slug === active && !c.route;
              return (
                <button
                  key={c.id}
                  onClick={() => pick(c)}
                  className={`relative group text-left rounded-2xl overflow-hidden p-[2px] transition-all ${isActive ? "scale-[1.02]" : "hover:scale-[1.01]"}`}
                  data-testid={`cmd-${c.id}`}
                >
                  <div className={`absolute inset-0 bg-gradient-to-br ${c.grad} ${isActive ? "opacity-100" : "opacity-80 group-hover:opacity-100"} transition-opacity`}/>
                  <div className={`relative rounded-[14px] px-3 md:px-4 py-3.5 md:py-4 ${isActive ? "bg-[#0a0908]" : "bg-[#0a0908]/85"}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-display text-lg md:text-xl leading-tight uppercase tracking-wide text-white">{c.label}</div>
                      {isOut && <span className="text-[9px] font-bold tracking-[0.2em] uppercase text-red-400">Sold Out</span>}
                    </div>
                    {p && p.has_capacity && !isOut && (
                      <div className="mt-1 text-[10px] tracking-[0.2em] uppercase text-[#e8c876]/85">{p.remaining_slots}/{p.total_slots} left</div>
                    )}
                    {p && !p.has_capacity && (
                      <div className="mt-1 text-[10px] tracking-[0.2em] uppercase text-[#e8c876]/85">Applications open</div>
                    )}
                    {c.route && (
                      <div className="mt-1 text-[10px] tracking-[0.2em] uppercase text-[#e8c876]/85">Request custom city</div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Active package detail */}
      <section ref={detailRef} className="max-w-[1200px] mx-auto px-5 pt-10 md:pt-14" data-testid="live-detail">
        {loading && <div className="py-12 text-center text-black/50">Loading auction…</div>}
        {!loading && pkg && (
          <motion.div
            key={pkg.slug}
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="rounded-3xl overflow-hidden bg-white shadow-[0_30px_80px_-40px_rgba(0,0,0,0.35)]"
          >
            <div className="relative aspect-[16/10] md:aspect-[21/9] overflow-hidden">
              <img src={IMAGES[pkg.slug]} alt={pkg.title} className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.7) 100%)" }}/>
              <div className="absolute top-3 left-3 flex items-center gap-2">
                <span className="text-[10px] tracking-[0.24em] uppercase font-bold px-3 py-1.5 rounded-full bg-white text-black">{pkg.city}</span>
                {pkg.highlight && <span className="text-[10px] tracking-[0.24em] uppercase font-bold px-3 py-1.5 rounded-full bg-[#c8102e] text-white">Featured</span>}
                {soldOut && <span className="text-[10px] tracking-[0.24em] uppercase font-bold px-3 py-1.5 rounded-full bg-black text-red-400" data-testid="sold-out-badge">SOLD OUT</span>}
              </div>
              <div className="absolute bottom-4 left-4 right-4 text-white">
                <div className="text-[10px] tracking-[0.28em] uppercase text-[#e8c876]/95">Now on the Live</div>
                <h2 className="mt-1 font-display text-3xl md:text-5xl leading-[0.95] tracking-[-0.02em]" data-testid="active-title">{pkg.title}</h2>
                {pkg.subtitle && <div className="text-white/85 text-sm md:text-base mt-1">{pkg.subtitle}</div>}
              </div>
            </div>

            <div className="p-5 md:p-8">
              {/* Stats row */}
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-2xl bg-[#f7f1e5] px-4 py-3">
                  <div className="text-[10px] tracking-[0.24em] uppercase text-black/55">Opening bid / Price</div>
                  <div className="stat-num text-2xl md:text-3xl text-[#0a0908]">{pkg.price_label || `$${pkg.opening_bid.toLocaleString()}`}</div>
                  <div className="text-[10px] tracking-[0.24em] uppercase text-black/50 mt-0.5">{pkg.window_label}</div>
                </div>
                <div className="rounded-2xl bg-[#f7f1e5] px-4 py-3">
                  <div className="text-[10px] tracking-[0.24em] uppercase text-black/55">Positions</div>
                  <div className="stat-num text-2xl md:text-3xl text-[#0a0908]" data-testid="live-remaining">
                    {pkg.has_capacity ? (soldOut ? "SOLD OUT" : `${pkg.remaining_slots} / ${pkg.total_slots}`) : "Open"}
                  </div>
                  {pkg.has_capacity && (
                    <div className="mt-1 h-1.5 w-full rounded-full bg-black/10 overflow-hidden">
                      <div className="h-full bg-[#c8102e]" style={{ width: `${Math.max(4, Math.round((pkg.remaining_slots / pkg.total_slots) * 100))}%` }}/>
                    </div>
                  )}
                </div>
              </div>

              {/* Countdown */}
              <div className="mt-4" data-testid="live-countdown">
                <div className="text-[10px] tracking-[0.28em] uppercase text-black/55 mb-2">Countdown to window close</div>
                <Countdown target={pkg.window_end} />
              </div>

              {/* Perks */}
              {pkg.perks && pkg.perks.length > 0 && (
                <div className="mt-6">
                  <div className="text-[10px] tracking-[0.28em] uppercase text-black/55 mb-2">Included Merchandise & Benefits</div>
                  <ul className="grid md:grid-cols-2 gap-1.5">
                    {pkg.perks.map((perk) => (
                      <li key={perk} className="text-sm text-black/80 flex items-start gap-2">
                        <span className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0 bg-[#c9a24b]"/> {perk}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {pkg.disclaimer && (
                <div className="mt-4 rounded-xl bg-[#7a1220]/5 border border-[#7a1220]/25 px-4 py-3 text-[13px] text-[#7a1220] italic">
                  {pkg.disclaimer}
                </div>
              )}

              {/* CTAs */}
              <div className="mt-6 grid md:grid-cols-2 gap-3">
                <button
                  onClick={openApply}
                  disabled={soldOut}
                  className="rounded-full py-4 text-[12px] tracking-[0.22em] uppercase font-bold transition-all disabled:opacity-40"
                  style={{ background: "linear-gradient(90deg,#e8c876,#c9a24b)", color: "#0a0908" }}
                  data-testid="live-apply"
                >
                  {soldOut ? "Sold Out" : `Apply — ${pkg.cta_label || "Submit Application"}`}
                </button>
                <button
                  onClick={openApply}
                  disabled={soldOut}
                  className="rounded-full py-4 text-[12px] tracking-[0.22em] uppercase font-bold transition-all disabled:opacity-40"
                  style={{ background: "linear-gradient(90deg,#c8102e,#7a1220)", color: "#ffffff" }}
                  data-testid="live-secure"
                >
                  {soldOut ? "Sold Out" : "Secure Reservation"}
                </button>
              </div>

              {/* Notice */}
              <div className="mt-6 rounded-2xl bg-[#0a0908] text-[#f7f1e5] px-4 py-4 text-[12px] md:text-[13px] leading-relaxed" data-testid="live-notice">
                <div className="text-[10px] tracking-[0.28em] uppercase text-[#e8c876] mb-1">Important</div>
                A TikTok comment or gift does not reserve a position. Your certificate is secured only after your application is approved, your dog qualifies and the required payment clears.
              </div>
            </div>
          </motion.div>
        )}
      </section>

      {/* Application form */}
      <ApplicationForm open={modal} onClose={() => { setModal(false); load(); }} pkg={pkg} source="tiktok-live" />

      {/* QR Overlay */}
      <AnimatePresence>
        {qr && pkg && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[92] bg-black flex items-center justify-center p-4"
            onClick={() => setQr(false)}
            data-testid="qr-overlay"
          >
            <div className="max-w-lg w-full text-center" onClick={(e) => e.stopPropagation()}>
              <div className="text-[10px] tracking-[0.28em] uppercase text-[#e8c876] mb-4">Scan · Send viewers to the active package</div>
              <div className="bg-white rounded-3xl p-6 md:p-8 inline-block">
                <img
                  alt={`QR code for ${pkg.title}`}
                  className="w-full max-w-[380px] h-auto block mx-auto"
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=800x800&margin=6&data=${encodeURIComponent(`${window.location.origin}/live?pkg=${pkg.slug}`)}`}
                  data-testid="qr-image"
                />
                <div className="mt-4 text-black font-display italic text-2xl">{pkg.title}</div>
                <div className="text-black/60 text-xs mt-1">torianosanzone.com/live?pkg={pkg.slug}</div>
              </div>
              <div className="mt-6 flex justify-center gap-3">
                <button onClick={() => setQr(false)} className="rounded-full px-6 py-3 bg-white text-black font-bold tracking-[0.22em] uppercase text-[11px]" data-testid="qr-close">Close</button>
                <button onClick={() => window.print()} className="rounded-full px-6 py-3 text-black font-bold tracking-[0.22em] uppercase text-[11px]" style={{ background: "linear-gradient(90deg,#e8c876,#c9a24b)" }} data-testid="qr-print">Print</button>
              </div>
              <div className="mt-3 text-[10px] tracking-[0.24em] uppercase text-white/60">Flash on camera during the Live</div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Sticky secure button */}
      <div className="fixed bottom-0 left-0 right-0 z-40 p-3 bg-gradient-to-t from-[#0a0908] via-[#0a0908]/95 to-transparent pointer-events-none" data-testid="live-sticky">
        <div className="pointer-events-auto flex gap-2">
          <button
            onClick={() => setQr(true)}
            className="rounded-full px-5 py-4 md:py-5 font-bold tracking-[0.22em] uppercase text-[12px] shadow-2xl bg-white text-black flex items-center gap-2"
            data-testid="qr-open"
            aria-label="Show QR code"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><line x1="14" y1="14" x2="14" y2="21"/><line x1="17.5" y1="14" x2="17.5" y2="17.5"/><line x1="21" y1="17.5" x2="17.5" y2="17.5"/><line x1="21" y1="21" x2="14" y2="21"/></svg>
            <span className="hidden sm:inline">QR</span>
          </button>
          <button
            onClick={openApply}
            disabled={soldOut}
            className="flex-1 rounded-full py-4 md:py-5 font-bold tracking-[0.22em] uppercase text-[13px] md:text-[14px] shadow-2xl disabled:opacity-50 flex items-center justify-center gap-2"
            style={{ background: "linear-gradient(90deg,#e8c876,#c9a24b)", color: "#0a0908" }}
            data-testid="live-sticky-btn"
          >
            {soldOut ? "Sold Out — pick another above" : "Secure My Certificate"}
            {!soldOut && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14M13 5l7 7-7 7"/></svg>}
          </button>
        </div>
      </div>
    </div>
  );
}
